import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { SavedSearchPermission } from "@prisma-postgres";
import { Control, Controller, FieldValues, useForm } from "react-hook-form";
import { RouterInput, RouterOutput, trpc } from "#next/utils/trpc";
import {
  FormModal,
  FormModalControls,
  FormModalTitle,
} from "#next/components/modals/FormModal";
import { RenderQuery } from "#next/components/base/RenderQuery";

type TSharingSettings = RouterOutput["savedSearch"]["sharingSettings"];

const UserForm = ({
  data,
  control,
}: {
  data: TSharingSettings;
  control: Control<FieldValues, any>;
}) => {
  const rows = data.userSharing.map((user) => {
    const defaultValue = user.permission ?? RadioFormOptions.NoAccess;
    return (
      <Grid container alignItems={"center"} key={user.id}>
        <Typography sx={{ mr: 4 }}>{user.name}</Typography>
        <Controller
          control={control}
          name={user.id}
          defaultValue={defaultValue}
          render={({ field }) => {
            return (
              <RadioGroup row {...field}>
                <FormControlLabel
                  value={RadioFormOptions.NoAccess}
                  control={<Radio />}
                  label="No Access"
                />
                <Tooltip
                  title={
                    <>
                      <Typography>- Can view project results</Typography>
                      <Typography>
                        - Can use this project in Project Editor
                      </Typography>
                    </>
                  }
                >
                  <FormControlLabel
                    value={RadioFormOptions.ReferenceAndView}
                    control={<Radio />}
                    label="View and Reference"
                  />
                </Tooltip>
              </RadioGroup>
            );
          }}
        ></Controller>
      </Grid>
    );
  });
  return <FormGroup sx={{ pl: 1, mt: 1 }}>{rows}</FormGroup>;
};

export const SharingModal = ({
  open,
  searchId,
  onClose,
}: {
  open: boolean;
  searchId: string;
  onClose: () => void;
}) => {
  return (
    <RenderQuery
      useQuery={trpc.savedSearch.sharingSettings}
      input={{
        savedSearchId: searchId,
      }}
      render={(data) => {
        return (
          <SharingModalUI
            open={open}
            sharingSettings={data}
            onClose={onClose}
            searchId={searchId}
          />
        );
      }}
    />
  );
};

const OrgForm = ({
  data,
  control,
}: {
  data: TSharingSettings;
  control: Control<IOrgFormValues, any>;
}) => {
  return (
    <Controller
      control={control}
      name="shareWithOrg"
      defaultValue={data.sharedWithOrg}
      render={({ field }) => {
        return (
          <FormGroup sx={{ pl: 1, mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => field.onChange(e.target.checked)}
                  checked={field.value}
                />
              }
              label={`Share with all ${data.orgName} users`}
            />
          </FormGroup>
        );
      }}
    ></Controller>
  );
};

const RadioFormOptions = {
  ...SavedSearchPermission,
  NoAccess: "no-access",
} as const;

type TUserFormValues = Record<
  string,
  (typeof RadioFormOptions)[keyof typeof RadioFormOptions]
>;

interface IOrgFormValues {
  shareWithOrg: boolean;
}

type TUserMutationInput = RouterInput["savedSearch"]["setSharedWithUserList"];

const SharingModalUI = ({
  open,
  sharingSettings,
  searchId,
  onClose,
}: {
  open: boolean;
  sharingSettings: TSharingSettings;
  searchId: string;
  onClose: () => void;
}) => {
  const userRHF = useForm<TUserFormValues>();
  const orgRHF = useForm<IOrgFormValues>();

  const orgSharingIsSelected = orgRHF.watch(
    "shareWithOrg",
    sharingSettings.sharedWithOrg,
  );

  let sharedUserList;

  if (orgSharingIsSelected) {
    sharedUserList = (
      <Typography sx={{ mt: 1 }}>
        Not available when project is shared with organization
      </Typography>
    );
  } else if (sharingSettings.userSharing.length === 0) {
    sharedUserList = (
      <Typography sx={{ mt: 1 }}>
        There are no other users in this organization
      </Typography>
    );
  } else {
    sharedUserList = (
      <UserForm data={sharingSettings} control={userRHF.control} />
    );
  }

  const saveSharedWithOrg = trpc.savedSearch.setSharedWithOrg.useMutation();
  const saveSharedWithUserList =
    trpc.savedSearch.setSharedWithUserList.useMutation();

  const trpcUtils = trpc.useContext();
  const handleSaveClick = () => {
    const onSuccess = (
      data: RouterOutput["savedSearch"]["setSharedWithOrg"],
    ) => {
      trpcUtils.savedSearch.sharingSettings.setData(
        { savedSearchId: searchId },
        data,
      );
      trpcUtils.savedSearch.canDelete.invalidate();
      onClose();
    };

    if (orgSharingIsSelected) {
      orgRHF.handleSubmit(() => {
        saveSharedWithOrg.mutate(
          { savedSearchId: searchId },
          {
            onSuccess,
          },
        );
      })();
    } else {
      userRHF.handleSubmit((data) => {
        const settings: TUserMutationInput["users"] = [];
        Object.entries(data).forEach(([userId, permission]) => {
          if (permission === RadioFormOptions.NoAccess) {
            return;
          }
          settings.push({ userId, permission });
        });
        saveSharedWithUserList.mutate(
          {
            savedSearchId: searchId,
            users: settings,
          },
          { onSuccess },
        );
      })();
    }
  };
  return (
    <FormModal open={open} onClose={onClose}>
      <Grid container direction="column">
        <Grid item style={{ maxHeight: 500, overflow: "scroll" }}>
          <FormModalTitle title="Project Sharing" />
          <Typography>
            These controls allow your project to be seen and used by other
            users. They are also able to use this project as part of their own
            searches.
          </Typography>
          <Typography variant="h4" sx={{ mt: 1 }}>
            Please Note
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 2 }}>
            <ListItem sx={{ display: "list-item", pl: 0 }}>
              Changes to a shared project can affect the output of other
              projects that reference it.
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0 }}>
              You will not be able to delete a project that you own if it is
              being used as part of another users project; you will need to
              reach out to that user and have them update their project(s) as
              appropriate.
            </ListItem>
            <ListItem sx={{ display: "list-item", pl: 0 }}>
              Removing access to a search will also cancel any subscriptions
              created by users who are losing access.
            </ListItem>
          </List>
          <Divider sx={{ mb: 2 }} />
          {/* TODO: Implement utilization list (names of users with searches that reference this search) */}
          {/* <Typography variant="h3" sx={{ mb: 1 }}>
              Current Utilization
            </Typography>
            <Typography>
              This search is referenced in searches owned by the following users
            </Typography>
            <Typography>-- list of user names here --</Typography>
            <Divider sx={{ my: 2 }} /> */}
          <Typography variant="h3" sx={{ mb: 1 }}>
            Share With Organization
          </Typography>
          <Typography>
            When this setting is selected all current and future users in your
            organization will be able to use this project.
          </Typography>
          <OrgForm data={sharingSettings} control={orgRHF.control} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="h3">Share With Specified Users</Typography>
          {sharedUserList}
        </Grid>
      </Grid>
      <FormModalControls
        onClose={onClose}
        onSave={handleSaveClick}
        saveIsDisabled={false}
        saveButtonName="Save"
      ></FormModalControls>
    </FormModal>
  );
};
